// Anonymous funnel beacons → Alt-API `POST /web/events` (plan §9).
//
// Hard rules this module keeps:
// - It can NEVER break or slow the AR experience: every call is wrapped, failures
//   are swallowed, and nothing is awaited.
// - Zero PII. `sessionId` is a random per-tab id, not a user id. No IP, no
//   coordinates, no names — only ids we already put in the URL, plus small
//   numeric/enum meta.
// - The web app still writes NO progress: this is a separate collection with its
//   own retention. Unity remains the sole writer of real progress.
import {ANALYTICS_URL} from './config'

export type WebEventType =
  | 'page_view'
  | 'ar_started'
  | 'aligned'
  | 'checkpoint_found'
  | 'completed'
  | 'cta_clicked'
  | 'fallback_preview'
  | 'error'
  | 'share_view'
  | 'share_ar_started'
  | 'share_placed'
  | 'share_cta_clicked'

type MetaValue = string | number | boolean | null
export interface TrackContext {
  alternativeId: string
  anchorId?: string | null
  meta?: Record<string, MetaValue>
}

const SESSION_KEY = 'webar_session_id'

const params = new URLSearchParams(window.location.search)
const disabled = params.get('noanalytics') === '1'
/** Where the visitor came from (?src=qr|share|poster…), kept for funnel splits. */
const src = params.get('src')
/**
 * Which physical sign was scanned (?loc=<code>, one code per sign placement).
 * Sent top-level (sibling of `src`) since Alt-API dfad3f5; events recorded
 * before that deploy carry it as `meta.loc`, so sign dashboards must read
 * both shapes until the retention TTL clears the old one.
 */
const loc = params.get('loc')?.slice(0, 64) || null

function sessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `s-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
    sessionStorage.setItem(SESSION_KEY, id)
    return id
  } catch {
    // Private mode / storage blocked — a per-call id still gives event counts,
    // just not session stitching.
    return `s-${Math.random().toString(36).slice(2)}`
  }
}

/** Fire-and-forget. Never throws, never awaited, never blocks a frame. */
export function track(type: WebEventType, ctx: TrackContext): void {
  if (disabled) return
  try {
    const meta = ctx.meta ?? {}
    const body = JSON.stringify({
      type,
      sessionId: sessionId(),
      alternativeId: String(ctx.alternativeId),
      ...(ctx.anchorId ? {anchorId: String(ctx.anchorId)} : {}),
      ...(src ? {src} : {}),
      ...(loc ? {loc} : {}),
      ts: new Date().toISOString(),
      ...(Object.keys(meta).length > 0 ? {meta} : {}),
    })

    // text/plain keeps this a CORS simple request (no preflight), which is what
    // makes cross-origin sendBeacon reliable — especially during page unload.
    const blob = new Blob([body], {type: 'text/plain;charset=UTF-8'})
    if (navigator.sendBeacon?.(ANALYTICS_URL, blob)) return

    void fetch(ANALYTICS_URL, {
      method: 'POST',
      body,
      headers: {'Content-Type': 'text/plain;charset=UTF-8'},
      keepalive: true,
      mode: 'no-cors',
    }).catch(() => undefined)
  } catch {
    // Analytics must never surface an error into the AR flow.
  }
}

/** Emit `type` at most once per page load (guards render-loop-driven events). */
export function trackOnce(type: WebEventType, ctx: TrackContext): void {
  if (fired.has(type)) return
  fired.add(type)
  track(type, ctx)
}
const fired = new Set<WebEventType>()
